// AssessmentSetupDrawer — slide-in panel for configuring an assessment stage.
// Opens when HR clicks "Configure" on any assessment stage in PipelineBuilder.
//
// Handles both:
//   - Questionnaire stages  → GET/PUT /questionnaire/:jobId/:stageId
//   - Exam/material stages  → GET/PUT /jobs/:jobId/pipeline-stage/:stageId/materials
//
// Tabs: Setup (time limit, passing score, rules) | Questions (builder) | Preview

import React, { useEffect, useState, useCallback } from "react";
import {
  X, Save, Loader2, Plus, Trash2, Check, Upload, RefreshCw,
  ChevronDown, ChevronUp, Eye, Settings, ListOrdered, FileText,
} from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";

const DEFAULT_RULES = [
  "Exam runs in fullscreen — do not exit",
  "Tab switching and copy/paste are monitored",
  "Your webcam is required for identity verification",
  "Exam auto-submits when time runs out",
  "Scores and results are shared only with the hiring team",
];

const Q_TYPES = [
  { value: "mcq",          label: "MCQ",          color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "multi_select", label: "Multi-select",  color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "true_false",   label: "True / False",  color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "short_answer", label: "Short Answer",  color: "bg-amber-50 text-amber-700 border-amber-200" },
];

const DIFFICULTIES = ["easy", "medium", "hard"];

let _qid = 0;
const newQid = () => `q-${Date.now()}-${_qid++}`;

function makeBlankQuestion() {
  return {
    _id: newQid(),
    question: "",
    questionType: "mcq",
    options: ["", ""],
    correctAnswer: null,
    correctAnswers: [],
    rubric: "",
    difficulty: "medium",
    points: 5,
  };
}

// ── QuestionCard ──────────────────────────────────────────────────────────────
function QuestionCard({ q, idx, onChange, onDelete }) {
  const [open, setOpen] = useState(idx === 0);

  const typeInfo = Q_TYPES.find(t => t.value === q.questionType) || Q_TYPES[0];

  const setField = (patch) => onChange({ ...q, ...patch });

  const setOption = (i, val) => {
    const opts = [...q.options];
    opts[i] = val;
    setField({ options: opts });
  };

  const addOption = () => setField({ options: [...q.options, ""] });

  const removeOption = (i) => {
    const opts = q.options.filter((_, idx) => idx !== i);
    // If removed option was the correctAnswer, clear it
    const patch = { options: opts };
    if (q.correctAnswer === q.options[i]) patch.correctAnswer = null;
    if (q.correctAnswers?.includes(q.options[i])) {
      patch.correctAnswers = q.correctAnswers.filter(a => a !== q.options[i]);
    }
    setField(patch);
  };

  const markCorrect = (opt) => {
    if (q.questionType === "multi_select") {
      const cur = q.correctAnswers || [];
      setField({ correctAnswers: cur.includes(opt) ? cur.filter(a => a !== opt) : [...cur, opt] });
    } else {
      setField({ correctAnswer: opt });
    }
  };

  const isCorrect = (opt) => {
    if (q.questionType === "multi_select") return (q.correctAnswers || []).includes(opt);
    return q.correctAnswer === opt;
  };

  const handleTypeChange = (newType) => {
    const patch = { questionType: newType, correctAnswer: null, correctAnswers: [] };
    if (newType === "true_false") patch.options = ["True", "False"];
    else if (q.questionType === "true_false") patch.options = ["", ""];
    setField(patch);
  };

  const preview = q.question?.trim() || "Untitled question";

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${open ? "border-meraki-indigo/30 shadow-meraki-soft" : "border-meraki-soft-blue"}`}>
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-meraki-soft-blue/30 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <span className="w-6 h-6 rounded-lg bg-meraki-soft-blue text-meraki-indigo text-xs font-bold flex items-center justify-center shrink-0">
          {idx + 1}
        </span>
        <span className="flex-1 text-sm text-meraki-graphite truncate font-medium">{preview}</span>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${typeInfo.color}`}>
            {typeInfo.label}
          </span>
          <span className="text-xs text-meraki-graphite/40">{q.points} pts</span>
          <button
            onClick={e => { e.stopPropagation(); onDelete(); }}
            className="p-1 text-meraki-graphite/30 hover:text-meraki-coral rounded transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {open ? <ChevronUp className="w-4 h-4 text-meraki-graphite/40" /> : <ChevronDown className="w-4 h-4 text-meraki-graphite/40" />}
        </div>
      </div>

      {/* Editor */}
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-meraki-soft-blue space-y-4 bg-white">
          {/* Question text */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-meraki-graphite/50">Question</label>
            <textarea
              value={q.question}
              onChange={e => setField({ question: e.target.value })}
              placeholder="Type your question here…"
              rows={2}
              className="mt-1 w-full border border-meraki-soft-blue rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-meraki-indigo/20 focus:border-meraki-indigo/40 resize-none font-outfit"
            />
          </div>

          {/* Type + Difficulty + Points */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-meraki-graphite/50">Type</label>
              <select
                value={q.questionType}
                onChange={e => handleTypeChange(e.target.value)}
                className="mt-1 w-full border border-meraki-soft-blue rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-meraki-indigo/40 bg-white font-outfit"
              >
                {Q_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-meraki-graphite/50">Difficulty</label>
              <select
                value={q.difficulty}
                onChange={e => setField({ difficulty: e.target.value })}
                className="mt-1 w-full border border-meraki-soft-blue rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-meraki-indigo/40 bg-white capitalize font-outfit"
              >
                {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-meraki-graphite/50">Points</label>
              <input
                type="number"
                min={1}
                value={q.points}
                onChange={e => setField({ points: Math.max(1, +e.target.value) })}
                className="mt-1 w-full border border-meraki-soft-blue rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-meraki-indigo/40 font-outfit"
              />
            </div>
          </div>

          {/* Options (MCQ / multi_select / true_false) */}
          {(q.questionType === "mcq" || q.questionType === "multi_select" || q.questionType === "true_false") && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-meraki-graphite/50 block mb-2">
                Options — click ✓ to mark{q.questionType === "multi_select" ? " all correct answers" : " the correct answer"}
              </label>
              <div className="space-y-2">
                {q.options.map((opt, oi) => {
                  const correct = isCorrect(opt);
                  return (
                    <div key={oi} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { if (opt.trim()) markCorrect(opt); }}
                        title="Mark as correct"
                        className={`w-7 h-7 rounded-lg border-2 shrink-0 flex items-center justify-center transition-all text-xs font-bold
                          ${correct
                            ? "bg-emerald-50 border-emerald-400 text-emerald-600"
                            : "border-meraki-soft-blue text-meraki-graphite/30 hover:border-emerald-300 hover:text-emerald-400"
                          }`}
                      >
                        ✓
                      </button>
                      {correct && (
                        <span className="text-[10px] font-semibold text-emerald-600 w-12 shrink-0">Correct</span>
                      )}
                      {!correct && <span className="w-12 shrink-0" />}
                      <input
                        type="text"
                        value={opt}
                        readOnly={q.questionType === "true_false"}
                        onChange={e => setOption(oi, e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                        className={`flex-1 border border-meraki-soft-blue rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-meraki-indigo/40 font-outfit
                          ${correct ? "bg-emerald-50 border-emerald-200" : "bg-white"}
                          ${q.questionType === "true_false" ? "text-meraki-graphite/60" : ""}
                        `}
                      />
                      {q.questionType !== "true_false" && q.options.length > 2 && (
                        <button onClick={() => removeOption(oi)} className="text-meraki-graphite/25 hover:text-meraki-coral shrink-0">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {q.questionType !== "true_false" && (
                <button onClick={addOption} className="mt-2 text-xs font-semibold text-meraki-indigo hover:text-meraki-coral flex items-center gap-1 transition-colors">
                  <Plus className="w-3 h-3" /> Add option
                </button>
              )}
            </div>
          )}

          {/* Rubric (short_answer) */}
          {q.questionType === "short_answer" && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-meraki-graphite/50 block mb-1">
                Model Answer / Grading Rubric
              </label>
              <textarea
                value={q.rubric}
                onChange={e => setField({ rubric: e.target.value })}
                placeholder="Describe what a good answer should include. AI will use this rubric to grade responses automatically."
                rows={3}
                className="w-full border border-meraki-soft-blue rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-meraki-indigo/20 focus:border-meraki-indigo/40 resize-none font-outfit"
              />
              <p className="text-[10px] text-meraki-graphite/40 mt-1">💡 AI grades this question automatically using the rubric above.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Drawer ───────────────────────────────────────────────────────────────
export default function AssessmentSetupDrawer({ jobId, stage, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState("setup");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Setup tab state
  const [timeLimit, setTimeLimit]         = useState(30);
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(true);
  const [passingScore, setPassingScore]   = useState(stage?.passingScore || 60);
  const [instructions, setInstructions]   = useState("");
  const [rules, setRules]                 = useState([...DEFAULT_RULES]);

  // Questions tab state
  const [questions, setQuestions] = useState([]);

  // Material upload (non-questionnaire assessment types)
  const [materialId, setMaterialId]   = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [generating, setGenerating]   = useState(false);

  const isQuestionnaire = stage?.type === "questionnaire";

  // ── Load existing data ──────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (isQuestionnaire) {
        const r = await api.get(`/questionnaire/${jobId}/${stage.stageId}`);
        const d = r.data?.data || {};
        setInstructions(d.instructions || "");
        setPassingScore(d.passingScore ?? stage?.passingScore ?? 60);
        setRules(d.examRules?.length ? d.examRules : [...DEFAULT_RULES]);
        setTimeLimitEnabled(!!d.timeLimit);
        setTimeLimit(d.timeLimit || 30);
        const qs = (d.questions || []).map(q => ({
          _id: q.questionId || newQid(),
          question: q.text || "",
          questionType: q.questionType === "text" ? "short_answer" : (q.questionType || "mcq"),
          options: q.options?.length ? q.options : ["", ""],
          correctAnswer: q.options?.[q.correctOption] ?? null,
          correctAnswers: [],
          rubric: q.keywords?.join(", ") || "",
          difficulty: q.difficulty || "medium",
          points: q.points || 5,
        }));
        setQuestions(qs);
      } else {
        const r = await api.get(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/materials`);
        const mat = r.data?.data;
        if (mat) {
          setMaterialId(mat._id);
          setInstructions(mat.candidateInstructions || "");
          setRules(mat.examRules?.length ? mat.examRules : [...DEFAULT_RULES]);
          setTimeLimitEnabled(!!mat.assessmentStructure?.timeLimit);
          setTimeLimit(mat.assessmentStructure?.timeLimit || 30);
          setPassingScore(mat.scoringLogic?.passingScore ?? stage?.passingScore ?? 60);
          const qs = (mat.generatedQuestions || []).map(q => ({
            _id: q._id || newQid(),
            question: q.question || "",
            questionType: q.questionType || "mcq",
            options: q.options?.length ? q.options : ["", ""],
            correctAnswer: q.correctAnswer || null,
            correctAnswers: q.correctAnswers || [],
            rubric: q.rubric || "",
            difficulty: q.difficulty || "medium",
            points: q.points || 5,
          }));
          setQuestions(qs);
        }
      }
    } catch (_) {
      // No data yet — start blank
    } finally {
      setLoading(false);
    }
  }, [jobId, stage?.stageId, isQuestionnaire]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      if (isQuestionnaire) {
        const payload = {
          title: stage.label,
          instructions,
          passingScore: Number(passingScore),
          timeLimit: timeLimitEnabled ? Number(timeLimit) : null,
          examRules: rules,
          questions: questions.map((q, i) => {
            const isChoice = q.questionType !== "short_answer";
            const optionIndex = isChoice ? q.options.indexOf(q.correctAnswer) : -1;
            return {
              questionId: q._id,
              questionType: q.questionType === "short_answer" ? "text" : "mcq",
              text: q.question,
              options: isChoice ? q.options : [],
              correctOption: optionIndex >= 0 ? optionIndex : 0,
              keywords: q.questionType === "short_answer"
                ? q.rubric.split(",").map(k => k.trim()).filter(Boolean)
                : [],
              points: q.points,
            };
          }),
        };
        await api.put(`/questionnaire/${jobId}/${stage.stageId}`, payload);
      } else {
        const genQs = questions.map(q => ({
          _id: q._id,
          question: q.question,
          questionType: q.questionType,
          options: q.options,
          correctAnswer: q.correctAnswer,
          correctAnswers: q.correctAnswers,
          rubric: q.rubric,
          difficulty: q.difficulty,
          points: q.points,
        }));
        const totalPoints = questions.reduce((s, q) => s + (q.points || 0), 0);

        if (materialId) {
          await api.put(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/materials/${materialId}`, {
            generatedQuestions: genQs,
            candidateInstructions: instructions,
            examRules: rules,
            scoringLogic: { totalPoints, passingScore: Number(passingScore), gradingMethod: "percentage" },
            assessmentStructure: { totalQuestions: questions.length, timeLimit: timeLimitEnabled ? Number(timeLimit) : null },
          });
        }
      }
      toast.success("Assessment saved");
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── PDF Upload ──────────────────────────────────────────────────────────────
  const handleUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("material", file);
    formData.append("designBrief", JSON.stringify({
      candidateInstructions: instructions,
      examRules: rules,
      passingCriteria: passingScore,
      timeLimit: timeLimitEnabled ? timeLimit : null,
    }));
    setUploading(true);
    try {
      await api.post(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/materials`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded — AI is generating questions…");
      setGenerating(true);
      setUploading(false);
      // Poll
      let attempts = 0;
      const interval = setInterval(async () => {
        attempts++;
        try {
          const r = await api.get(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/materials`);
          const mat = r.data?.data;
          if (mat?.status === "ready") {
            clearInterval(interval);
            setGenerating(false);
            setMaterialId(mat._id);
            const qs = (mat.generatedQuestions || []).map(q => ({
              _id: q._id || newQid(),
              question: q.question || "",
              questionType: q.questionType || "mcq",
              options: q.options?.length ? q.options : ["", ""],
              correctAnswer: q.correctAnswer || null,
              correctAnswers: q.correctAnswers || [],
              rubric: q.rubric || "",
              difficulty: q.difficulty || "medium",
              points: q.points || 5,
            }));
            setQuestions(qs);
            setActiveTab("questions");
            toast.success(`${qs.length} questions generated — review and adjust below`);
          } else if (mat?.status === "failed" || attempts > 30) {
            clearInterval(interval);
            setGenerating(false);
            toast.error(mat?.errorMessage || "Generation failed");
          }
        } catch { if (attempts > 30) clearInterval(interval); }
      }, 3000);
    } catch (e) {
      setUploading(false);
      toast.error("Upload failed: " + (e.response?.data?.message || e.message));
    }
  };

  const addQuestion = () => setQuestions(qs => [...qs, makeBlankQuestion()]);
  const updateQuestion = (idx, q) => setQuestions(qs => qs.map((old, i) => i === idx ? q : old));
  const deleteQuestion = (idx) => setQuestions(qs => qs.filter((_, i) => i !== idx));

  const totalPoints = questions.reduce((s, q) => s + (q.points || 0), 0);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}
      <div className="flex-1 bg-black/40" onClick={onClose} />

      {/* Drawer */}
      <div className="w-full max-w-2xl bg-white flex flex-col shadow-2xl h-full overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-5 pb-0 border-b border-meraki-soft-blue shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div>
                <h2 className="font-bold text-meraki-indigo text-lg leading-tight">{stage?.label}</h2>
                <p className="text-xs text-meraki-graphite/50 mt-0.5 capitalize">{stage?.type?.replace(/_/g, " ")} · Assessment</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="btn-meraki-ghost text-sm flex items-center gap-1.5">
                <X className="w-4 h-4" /> Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="btn-meraki text-sm flex items-center gap-1.5 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-0">
            {[
              { id: "setup",     label: "Setup",     icon: Settings },
              { id: "questions", label: `Questions${questions.length ? ` (${questions.length})` : ""}`, icon: ListOrdered },
              { id: "preview",   label: "Preview",   icon: Eye },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === id
                    ? "border-meraki-indigo text-meraki-indigo"
                    : "border-transparent text-meraki-graphite/50 hover:text-meraki-graphite"
                  }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-meraki-indigo/60">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* ── SETUP TAB ── */}
            {activeTab === "setup" && (
              <>
                <section className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-meraki-graphite/50 pb-2 border-b border-meraki-soft-blue">
                    Timing &amp; Scoring
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-meraki-graphite/60 block mb-1.5">Time Limit</label>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setTimeLimitEnabled(e => !e)}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors shrink-0
                            ${timeLimitEnabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                        >
                          {timeLimitEnabled ? "On" : "Off"}
                        </button>
                        {timeLimitEnabled && (
                          <div className="flex items-center gap-1 flex-1">
                            <input
                              type="number"
                              min={5}
                              max={180}
                              value={timeLimit}
                              onChange={e => setTimeLimit(+e.target.value)}
                              className="w-full border border-meraki-soft-blue rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-meraki-indigo/40"
                            />
                            <span className="text-xs text-meraki-graphite/50 shrink-0">min</span>
                          </div>
                        )}
                        {!timeLimitEnabled && (
                          <span className="text-xs text-meraki-graphite/40">No time limit</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-meraki-graphite/60 block mb-1.5">Passing Score (%)</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={passingScore}
                        onChange={e => setPassingScore(+e.target.value)}
                        className="w-full border border-meraki-soft-blue rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-meraki-indigo/40"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-meraki-graphite/60 block mb-1.5">Candidate Instructions</label>
                    <textarea
                      value={instructions}
                      onChange={e => setInstructions(e.target.value)}
                      placeholder="Instructions shown to the candidate before starting the assessment…"
                      rows={3}
                      className="w-full border border-meraki-soft-blue rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-meraki-indigo/20 focus:border-meraki-indigo/40 resize-none font-outfit"
                    />
                  </div>
                </section>

                <section className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-meraki-graphite/50 pb-2 border-b border-meraki-soft-blue">
                    Exam Rules
                  </h3>
                  <div className="space-y-2">
                    {rules.map((rule, ri) => (
                      <div key={ri} className="flex items-center gap-2">
                        <span className="text-xs text-meraki-graphite/30 w-5 shrink-0 text-right">{ri + 1}.</span>
                        <input
                          value={rule}
                          onChange={e => {
                            const next = [...rules];
                            next[ri] = e.target.value;
                            setRules(next);
                          }}
                          placeholder="Enter exam rule…"
                          className="flex-1 border border-meraki-soft-blue rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-meraki-indigo/40 font-outfit"
                        />
                        <button
                          onClick={() => setRules(rules.filter((_, i) => i !== ri))}
                          className="text-meraki-graphite/25 hover:text-meraki-coral shrink-0 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setRules([...rules, ""])}
                    className="flex items-center gap-1.5 text-xs font-semibold text-meraki-indigo hover:text-meraki-coral transition-colors border border-dashed border-meraki-soft-blue hover:border-meraki-coral rounded-xl px-4 py-2 w-full justify-center"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Rule
                  </button>
                </section>
              </>
            )}

            {/* ── QUESTIONS TAB ── */}
            {activeTab === "questions" && (
              <>
                {/* Upload banner for non-questionnaire types */}
                {!isQuestionnaire && (
                  <div className="border border-dashed border-meraki-soft-blue rounded-xl p-4 bg-meraki-soft-blue/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-meraki-graphite">
                          {questions.length > 0 ? "AI-generated questions below" : "Upload a PDF to auto-generate questions"}
                        </p>
                        <p className="text-xs text-meraki-graphite/50 mt-0.5">
                          {questions.length > 0
                            ? "You can edit any question, change correct answers, or add new ones manually."
                            : "Or skip the upload and add questions manually below."}
                        </p>
                      </div>
                      <div>
                        {generating ? (
                          <span className="flex items-center gap-1.5 text-xs text-orange-500 font-medium">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…
                          </span>
                        ) : uploading ? (
                          <span className="flex items-center gap-1.5 text-xs text-meraki-indigo font-medium">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…
                          </span>
                        ) : (
                          <label className="flex items-center gap-1.5 px-3 py-1.5 bg-meraki-indigo text-white rounded-lg text-xs font-semibold cursor-pointer hover:bg-meraki-indigo/90 transition-colors">
                            <Upload className="w-3.5 h-3.5" />
                            {questions.length > 0 ? "Re-upload" : "Upload PDF"}
                            <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden"
                              onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }} />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats bar */}
                {questions.length > 0 && (
                  <div className="flex items-center gap-4 text-xs text-meraki-graphite/50 bg-meraki-soft-blue/30 rounded-xl px-4 py-2">
                    <span><strong className="text-meraki-indigo">{questions.length}</strong> questions</span>
                    <span><strong className="text-meraki-indigo">{totalPoints}</strong> total pts</span>
                    <span>Pass at <strong className="text-meraki-indigo">{passingScore}%</strong> = {Math.ceil(totalPoints * passingScore / 100)} pts</span>
                  </div>
                )}

                {/* Question cards */}
                <div className="space-y-3">
                  {questions.map((q, idx) => (
                    <QuestionCard
                      key={q._id}
                      q={q}
                      idx={idx}
                      onChange={updated => updateQuestion(idx, updated)}
                      onDelete={() => deleteQuestion(idx)}
                    />
                  ))}
                </div>

                {questions.length === 0 && !generating && (
                  <div className="text-center py-8 text-meraki-graphite/40 text-sm">
                    No questions yet.{!isQuestionnaire ? " Upload a PDF or add manually." : " Add your first question below."}
                  </div>
                )}

                <button
                  onClick={addQuestion}
                  className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-meraki-soft-blue hover:border-meraki-indigo/40 hover:bg-meraki-soft-blue/30 rounded-xl py-3 text-sm font-semibold text-meraki-indigo/60 hover:text-meraki-indigo transition-all"
                >
                  <Plus className="w-4 h-4" /> Add Question
                </button>
              </>
            )}

            {/* ── PREVIEW TAB ── */}
            {activeTab === "preview" && (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-700 font-medium">
                  👁 This is what the candidate sees. Correct answers are hidden.
                </div>
                <div className="border border-meraki-soft-blue rounded-xl p-5 space-y-4">
                  <div>
                    <h3 className="font-bold text-meraki-indigo text-base">{stage?.label}</h3>
                    {instructions && <p className="text-sm text-meraki-graphite/60 mt-1">{instructions}</p>}
                  </div>
                  <div className="flex gap-4 text-xs text-meraki-graphite/50">
                    {timeLimitEnabled && <span>⏱ {timeLimit} min</span>}
                    <span>📋 {questions.length} question{questions.length !== 1 ? "s" : ""}</span>
                    <span>✅ Pass at {passingScore}%</span>
                    <span>🏆 {totalPoints} pts total</span>
                  </div>
                  {questions.length > 0 && (
                    <div className="border border-meraki-soft-blue rounded-xl p-4 space-y-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-meraki-graphite/40">
                        Question 1 of {questions.length} · {questions[0].points} pts
                      </p>
                      <p className="text-sm font-medium text-meraki-graphite">{questions[0].question || "(no question text)"}</p>
                      {(questions[0].questionType === "mcq" || questions[0].questionType === "multi_select" || questions[0].questionType === "true_false") && (
                        <div className="space-y-2">
                          {questions[0].options.map((opt, i) => (
                            <div key={i} className="flex items-center gap-2.5 text-sm text-meraki-graphite/70">
                              <div className="w-4 h-4 rounded-full border-2 border-meraki-soft-blue bg-meraki-soft-blue/30 shrink-0" />
                              {opt || `Option ${String.fromCharCode(65 + i)}`}
                            </div>
                          ))}
                        </div>
                      )}
                      {questions[0].questionType === "short_answer" && (
                        <div className="border border-meraki-soft-blue rounded-lg px-3 py-6 text-xs text-meraki-graphite/30 bg-meraki-soft-blue/20">
                          Candidate types their answer here…
                        </div>
                      )}
                    </div>
                  )}
                  {rules.length > 0 && (
                    <div className="border-t border-meraki-soft-blue pt-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-meraki-graphite/40 mb-2">Exam Rules</p>
                      <ul className="space-y-1">
                        {rules.map((r, i) => (
                          <li key={i} className="text-xs text-meraki-graphite/60 flex gap-2">
                            <span className="text-meraki-graphite/30">{i + 1}.</span> {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
